/**
 * CAREER ROSTER — saved pilots, in `localStorage`.
 *
 * The original keeps up to **15 pilot careers at once** and refuses a
 * sixteenth with `THE CAREER ROSTER IS FULL!` (manual, "Start a career"); it
 * also *"automatically saves upon exiting the Aerodrome menu"* rather than
 * asking. Both behaviours are kept: every transition writes, and the roster is
 * capped.
 *
 * Storage-agnostic on purpose — `CareerStore` is just `getItem`/`setItem`/
 * `removeItem`, so the round-trip is unit-testable against a plain in-memory
 * fake (`bun test` has no DOM), exactly as `audio/settings.ts` does it.
 *
 * One entry holds the career *and* the last mission's consequences, because the
 * consequences screen has to survive the navigation back from `/fly` — the
 * flight route applies the debriefing and saves, and the career screen reads
 * what happened out of the save rather than out of router state that a reload
 * would lose.
 */

import { parseCareer, type CareerConsequence, type CareerState, type MissionDebrief } from "@red-opus/engine";

export const CAREER_STORAGE_KEY = "red-opus/careers";
export const ACTIVE_PILOT_KEY = "red-opus/active-pilot";

/** The manual's own roster cap. */
export const MAX_PILOTS = 15;

export interface CareerStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CareerSave {
  readonly career: CareerState;
  /** What the last sortie did to him, for the consequences screen. */
  readonly lastConsequence: CareerConsequence | undefined;
  /**
   * The last sortie's own debriefing, for the `MISSION DEBRIEFING` screen. The
   * consequences record what the *career* made of the sortie; this is what the
   * sortie itself did, in the fields the original's screen prints. Optional and
   * additive: a save written before this existed simply has no debriefing, and
   * the screen falls through to the aerodrome.
   */
  readonly lastDebrief: MissionDebrief | undefined;
  /** ISO timestamp of the last write, for ordering the roster. */
  readonly updatedAt: string;
}

function asSave(value: unknown): CareerSave | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const raw = value as { career?: unknown; lastConsequence?: unknown; lastDebrief?: unknown; updatedAt?: unknown };
  const career = parseCareer(JSON.stringify(raw.career ?? null));
  if (!career) return undefined;
  return {
    career,
    lastConsequence: (raw.lastConsequence as CareerConsequence | undefined) ?? undefined,
    lastDebrief: (raw.lastDebrief as MissionDebrief | undefined) ?? undefined,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : "",
  };
}

/** Every readable save. Unreadable ones are dropped rather than crashing the roster. */
export function loadCareers(store: CareerStore): readonly CareerSave[] {
  const raw = store.getItem(CAREER_STORAGE_KEY);
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.map(asSave).filter((save): save is CareerSave => save !== undefined);
}

function writeAll(store: CareerStore, saves: readonly CareerSave[]): void {
  store.setItem(CAREER_STORAGE_KEY, JSON.stringify(saves));
}

export function findCareer(store: CareerStore, id: string): CareerSave | undefined {
  return loadCareers(store).find((save) => save.career.id === id);
}

export function rosterIsFull(store: CareerStore): boolean {
  return loadCareers(store).length >= MAX_PILOTS;
}

/** Verbatim, for the screen that has to say no. */
export const ROSTER_FULL_MESSAGE = "THE CAREER ROSTER IS FULL!";

export interface SaveOptions {
  readonly consequence?: CareerConsequence | undefined;
  readonly debrief?: MissionDebrief | undefined;
  /** Overridable so a test does not depend on the clock. */
  readonly now?: string;
}

/**
 * Writes a career, replacing any earlier save of the same pilot. Refuses a new
 * pilot when the roster is full, the way the original does.
 */
export function saveCareer(store: CareerStore, career: CareerState, options: SaveOptions = {}): void {
  const existing = loadCareers(store);
  const known = existing.some((save) => save.career.id === career.id);
  if (!known && existing.length >= MAX_PILOTS) throw new Error(ROSTER_FULL_MESSAGE);
  const entry: CareerSave = {
    career,
    lastConsequence: options.consequence,
    lastDebrief: options.debrief,
    updatedAt: options.now ?? new Date().toISOString(),
  };
  writeAll(store, known ? existing.map((save) => (save.career.id === career.id ? entry : save)) : [...existing, entry]);
}

export function deleteCareer(store: CareerStore, id: string): void {
  writeAll(store, loadCareers(store).filter((save) => save.career.id !== id));
  if (store.getItem(ACTIVE_PILOT_KEY) === id) store.removeItem(ACTIVE_PILOT_KEY);
}

export function setActivePilot(store: CareerStore, id: string | undefined): void {
  if (id === undefined) store.removeItem(ACTIVE_PILOT_KEY);
  else store.setItem(ACTIVE_PILOT_KEY, id);
}

export function activePilot(store: CareerStore): CareerSave | undefined {
  const id = store.getItem(ACTIVE_PILOT_KEY);
  return id === null ? undefined : findCareer(store, id);
}

/** The browser's `localStorage`, or nothing when there isn't one (SSR, tests). */
export function browserCareerStore(): CareerStore | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** A seed for a new pilot. Not from the career layer: the *choice* of seed is the app's. */
export function newCareerSeed(now: number = Date.now()): number {
  return (now >>> 0) ^ 0x5f3759df;
}
